import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AdminActivities } from "./AdminActivities";
import { AdminContent, NEWS_CONFIG, EVENTS_CONFIG, PROJECTS_CONFIG, FAQ_CONFIG, NOTICES_CONFIG, PRESS_CONFIG, PRODUCTS_CONFIG } from "./AdminContent";
import { AdminOrders } from "./AdminOrders";
import { AdminDonations } from "./AdminDonations";
import { AdminMetrics } from "./AdminMetrics";
import {
  Lock,
  LogOut,
  Users,
  AlertTriangle,
  Flame,
  MessageSquare,
  CalendarCheck,
  Store,
  ShoppingBag,
  RefreshCw,
  ArrowLeft,
  Loader2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "perasciano_admin_token";

const REPORT_LABELS = {
  zona_da_pulire: "Zona da pulire",
  potature_smaltimento: "Potature da smaltire",
  persona_difficolta: "Persona in difficoltà",
  animale_difficolta: "Animale in difficoltà",
  pericolo_incendio: "Pericolo incendio",
  manutenzione_territorio: "Manutenzione territorio",
  altro: "Altro",
};

const ROLE_LABELS = {
  operativo_campo: "Operativo sul campo",
  coordinatore: "Coordinatore",
  comunicazione: "Comunicazione / Social",
  logistica: "Logistica / Trasporti",
  esperto_tecnico: "Esperto tecnico",
  supporto_persone: "Supporto alle persone",
  altro: "Altro ruolo",
};

const AVAIL_LABELS = {
  occasionale: "Occasionale",
  settimanale: "Settimanale",
  costante: "Costante",
  emergenze: "Solo emergenze",
};

const DAMAGE_LABELS = {
  abitazione: "Abitazione",
  terreno_agricolo: "Terreno agricolo",
  animali_allevamento: "Animali / allevamento",
  attivita_commerciale: "Attività commerciale",
  veicoli_mezzi: "Veicoli / mezzi",
  boschi_uliveto: "Bosco / uliveto",
  altro: "Altro",
};

const URGENCY_COLORS = {
  alta: "bg-red-100 text-red-800 border border-red-200",
  media: "bg-amber-100 text-amber-800 border border-amber-200",
  bassa: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
};

const LoginScreen = ({ onAuthed }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Password non valida");
      }
      const data = await res.json();
      localStorage.setItem(STORAGE_KEY, data.token);
      toast.success("Accesso effettuato");
      onAuthed(data.token);
    } catch (err) {
      toast.error(err.message || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a2620] via-[#0f1a14] to-[#0a1310] px-4"
      data-testid="admin-login-screen"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#C9D6CC] hover:text-white text-sm mb-6 transition"
          data-testid="admin-back-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna al sito
        </Link>
        <div className="bg-[#0f1a14]/90 backdrop-blur border border-[#2E4F3B]/40 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-[#2E4F3B] flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white text-xl font-semibold tracking-tight">
                Area riservata
              </h1>
              <p className="text-[#9AB0A1] text-xs">Per Asciano · Dashboard</p>
            </div>
          </div>
          <p className="text-[#C9D6CC] text-sm mb-6 mt-4">
            Inserisci la password admin per visualizzare le iscrizioni e le
            segnalazioni.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[#C9D6CC] text-xs uppercase tracking-wide mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                className="w-full bg-[#1a2620] border border-[#2E4F3B]/60 rounded-lg px-4 py-3 text-white placeholder:text-[#5C7060] focus:outline-none focus:border-[#7BA577] transition"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#7BA577] hover:bg-[#6B9067] disabled:opacity-60 text-[#0a1310] font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
              data-testid="admin-login-button"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Lock className="w-4 h-4" />
              )}
              {loading ? "Accesso in corso..." : "Entra"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div
    className="bg-white border border-[#E5E1D8] rounded-xl p-5 shadow-sm hover:shadow-md transition"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[#5C5C5C] text-xs uppercase tracking-wide font-medium">
          {label}
        </p>
        <p className="text-3xl font-bold text-[#1a2620] mt-1 tabular-nums">
          {value ?? "—"}
        </p>
      </div>
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: color + "20", color }}
      >
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const Section = ({ title, icon: Icon, count, children, testid }) => (
  <section
    className="bg-white border border-[#E5E1D8] rounded-xl shadow-sm overflow-hidden"
    data-testid={testid}
  >
    <header className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1D8] bg-[#FBF9F6]">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-[#2E4F3B]" />
        <h2 className="text-[#1a2620] font-semibold">{title}</h2>
        <span className="text-[#5C5C5C] text-sm tabular-nums">({count})</span>
      </div>
    </header>
    <div className="overflow-x-auto">{children}</div>
  </section>
);

const EmptyRow = ({ cols }) => (
  <tr>
    <td
      colSpan={cols}
      className="text-center text-[#8A8A8A] text-sm py-8 italic"
    >
      Nessun dato disponibile
    </td>
  </tr>
);

const Th = ({ children, className = "" }) => (
  <th
    className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#5C5C5C] ${className}`}
  >
    {children}
  </th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-sm text-[#1a2620] align-top ${className}`}>
    {children}
  </td>
);

const UrgencyBadge = ({ value }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium uppercase ${
      URGENCY_COLORS[value] || "bg-gray-100 text-gray-800"
    }`}
  >
    {value}
  </span>
);

const Dashboard = ({ token, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({
    contacts: [],
    reports: [],
    volunteers: [],
    fireDamage: [],
    rsvps: [],
    partners: [],
    orders: [],
  });

  const fetchAll = useCallback(async () => {
    const headers = { "X-Admin-Password": token };
    const safe = async (path) => {
      try {
        const res = await fetch(`${BACKEND}${path}`, { headers });
        if (res.status === 401) throw new Error("UNAUTHORIZED");
        if (!res.ok) return null;
        return await res.json();
      } catch (e) {
        if (e.message === "UNAUTHORIZED") throw e;
        return null;
      }
    };
    try {
      const [summary, contacts, reports, volunteers, fireDamage, rsvps, partners, orders] =
        await Promise.all([
          safe("/api/admin/summary"),
          safe("/api/contact"),
          safe("/api/reports?limit=500"),
          safe("/api/volunteers?limit=500"),
          safe("/api/fire-damage?limit=500"),
          safe("/api/admin/event-rsvps?limit=500"),
          safe("/api/partners?limit=500"),
          safe("/api/admin/shop-orders?limit=500"),
        ]);
      setSummary(summary || {});
      setData({
        contacts: contacts || [],
        reports: reports || [],
        volunteers: volunteers || [],
        fireDamage: fireDamage || [],
        rsvps: rsvps || [],
        partners: partners || [],
        orders: orders || [],
      });
    } catch (e) {
      if (e.message === "UNAUTHORIZED") {
        toast.error("Sessione scaduta. Effettua di nuovo l'accesso.");
        onLogout();
      } else {
        toast.error("Errore nel caricamento dei dati");
      }
    }
  }, [token, onLogout]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchAll();
      setLoading(false);
    })();
  }, [fetchAll]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
    toast.success("Dati aggiornati");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF9F6]">
        <div className="flex items-center gap-3 text-[#2E4F3B]">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Caricamento dashboard…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F6]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-[#0f1a14] text-white border-b border-[#2E4F3B]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-[#9AB0A1] hover:text-white transition"
              data-testid="admin-home-link"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">
                Dashboard Per Asciano
              </h1>
              <p className="text-[#9AB0A1] text-xs">Area riservata · admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 bg-[#1a2620] hover:bg-[#243429] border border-[#2E4F3B]/60 px-3 py-2 rounded-lg text-sm transition disabled:opacity-50"
              data-testid="admin-refresh-button"
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Aggiorna</span>
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 bg-red-600/90 hover:bg-red-600 px-3 py-2 rounded-lg text-sm transition"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            icon={Users}
            label="Volontari"
            value={summary?.volunteers}
            color="#2E4F3B"
            testid="stat-volunteers"
          />
          <StatCard
            icon={AlertTriangle}
            label="Segnalazioni"
            value={summary?.reports}
            color="#C19A3D"
            testid="stat-reports"
          />
          <StatCard
            icon={Flame}
            label="Danni incendio"
            value={summary?.fire_damage}
            color="#C15C3D"
            testid="stat-fire-damage"
          />
          <StatCard
            icon={MessageSquare}
            label="Contatti"
            value={summary?.contacts}
            color="#3D6BC1"
            testid="stat-contacts"
          />
          <StatCard
            icon={CalendarCheck}
            label="RSVP evento"
            value={summary?.event_rsvps}
            color="#8E3DC1"
            testid="stat-rsvps"
          />
        </div>

        {/* Volunteers */}
        <AdminActivities token={token} />

        {/* Gestione contenuti dinamici */}
        <AdminMetrics token={token} />
        <AdminContent token={token} config={NOTICES_CONFIG} />
        <AdminContent token={token} config={NEWS_CONFIG} />
        <AdminContent token={token} config={PRESS_CONFIG} />
        <AdminContent token={token} config={EVENTS_CONFIG} />
        <AdminContent token={token} config={PROJECTS_CONFIG} />
        <AdminContent token={token} config={PRODUCTS_CONFIG} />
        <AdminContent token={token} config={FAQ_CONFIG} />

        {/* Ordini shop */}
        <AdminOrders token={token} />

        {/* Donazioni */}
        <AdminDonations token={token} />

        <Section
          title="Volontari"
          icon={Users}
          count={data.volunteers.length}
          testid="section-volunteers"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F6] border-b border-[#E5E1D8]">
              <tr>
                <Th>Data</Th>
                <Th>Nome</Th>
                <Th>Telefono</Th>
                <Th>Email</Th>
                <Th>Ruolo</Th>
                <Th>Disponibilità</Th>
                <Th>Attività</Th>
                <Th>Messaggio</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1D8]">
              {data.volunteers.length === 0 ? (
                <EmptyRow cols={8} />
              ) : (
                data.volunteers.map((v) => (
                  <tr key={v.id} className="hover:bg-[#FBF9F6]/60">
                    <Td className="whitespace-nowrap text-[#5C5C5C]">
                      {fmtDate(v.created_at)}
                    </Td>
                    <Td className="font-medium">{v.name}</Td>
                    <Td className="font-mono text-[13px]">{v.phone}</Td>
                    <Td className="text-[#5C5C5C]">{v.email || "—"}</Td>
                    <Td>{ROLE_LABELS[v.role] || v.role}</Td>
                    <Td>{AVAIL_LABELS[v.availability] || v.availability}</Td>
                    <Td className="max-w-[200px]">
                      <div className="flex flex-wrap gap-1">
                        {(v.activities || []).slice(0, 3).map((a) => (
                          <span
                            key={a}
                            className="text-[11px] bg-[#E8EFE8] text-[#2E4F3B] px-1.5 py-0.5 rounded"
                          >
                            {a.replace(/_/g, " ")}
                          </span>
                        ))}
                        {(v.activities || []).length > 3 && (
                          <span className="text-[11px] text-[#5C5C5C]">
                            +{v.activities.length - 3}
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td className="max-w-[260px] text-[#5C5C5C] whitespace-pre-wrap">
                      {v.message || "—"}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>

        {/* Partners */}
        <Section
          title="Partner / Aziende"
          icon={Store}
          count={data.partners.length}
          testid="section-partners"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F6] border-b border-[#E5E1D8]">
              <tr>
                <Th>Data</Th>
                <Th>Attività</Th>
                <Th>Referente</Th>
                <Th>Email</Th>
                <Th>Telefono</Th>
                <Th>Tipo</Th>
                <Th>Contributo</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1D8]">
              {data.partners.length === 0 ? (
                <EmptyRow cols={7} />
              ) : (
                data.partners.map((p) => (
                  <tr key={p.id} className="hover:bg-[#FBF9F6]/60">
                    <Td className="whitespace-nowrap text-[#5C5C5C]">
                      {fmtDate(p.created_at)}
                    </Td>
                    <Td className="font-medium">{p.business_name}</Td>
                    <Td>{p.contact_name}</Td>
                    <Td className="text-[#5C5C5C]">{p.email}</Td>
                    <Td className="font-mono text-[13px]">
                      {p.phone || "—"}
                    </Td>
                    <Td className="text-[#5C5C5C]">
                      {p.activity_type || "—"}
                    </Td>
                    <Td className="max-w-[320px] text-[#5C5C5C] whitespace-pre-wrap">
                      {p.contribution}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>

        {/* Fire damage */}
        <Section
          title="Danni da incendio"
          icon={Flame}
          count={data.fireDamage.length}
          testid="section-fire-damage"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F6] border-b border-[#E5E1D8]">
              <tr>
                <Th>Data</Th>
                <Th>Nome</Th>
                <Th>Contatto</Th>
                <Th>Località</Th>
                <Th>Tipi danno</Th>
                <Th>Urgenza</Th>
                <Th>Descrizione</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1D8]">
              {data.fireDamage.length === 0 ? (
                <EmptyRow cols={7} />
              ) : (
                data.fireDamage.map((d) => (
                  <tr key={d.id} className="hover:bg-[#FBF9F6]/60">
                    <Td className="whitespace-nowrap text-[#5C5C5C]">
                      {fmtDate(d.created_at)}
                    </Td>
                    <Td className="font-medium">{d.name}</Td>
                    <Td className="font-mono text-[13px]">{d.contact}</Td>
                    <Td>{d.location}</Td>
                    <Td className="max-w-[220px]">
                      <div className="flex flex-wrap gap-1">
                        {(d.damage_types || []).map((t) => (
                          <span
                            key={t}
                            className="text-[11px] bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded"
                          >
                            {DAMAGE_LABELS[t] || t}
                          </span>
                        ))}
                      </div>
                    </Td>
                    <Td>
                      <UrgencyBadge value={d.urgency} />
                    </Td>
                    <Td className="max-w-[320px] text-[#5C5C5C] whitespace-pre-wrap">
                      {d.description}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>

        {/* Reports */}
        <Section
          title="Segnalazioni territorio"
          icon={AlertTriangle}
          count={data.reports.length}
          testid="section-reports"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F6] border-b border-[#E5E1D8]">
              <tr>
                <Th>Data</Th>
                <Th>Tipo</Th>
                <Th>Località</Th>
                <Th>Urgenza</Th>
                <Th>Nome</Th>
                <Th>Contatto</Th>
                <Th>Descrizione</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1D8]">
              {data.reports.length === 0 ? (
                <EmptyRow cols={7} />
              ) : (
                data.reports.map((r) => (
                  <tr key={r.id} className="hover:bg-[#FBF9F6]/60">
                    <Td className="whitespace-nowrap text-[#5C5C5C]">
                      {fmtDate(r.created_at)}
                    </Td>
                    <Td>{REPORT_LABELS[r.type] || r.type}</Td>
                    <Td>{r.location}</Td>
                    <Td>
                      <UrgencyBadge value={r.urgency} />
                    </Td>
                    <Td>{r.name || "—"}</Td>
                    <Td className="font-mono text-[13px]">
                      {r.contact || "—"}
                    </Td>
                    <Td className="max-w-[320px] text-[#5C5C5C] whitespace-pre-wrap">
                      {r.description}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>

        {/* Contacts */}
        <Section
          title="Messaggi di contatto"
          icon={MessageSquare}
          count={data.contacts.length}
          testid="section-contacts"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F6] border-b border-[#E5E1D8]">
              <tr>
                <Th>Data</Th>
                <Th>Nome</Th>
                <Th>Email</Th>
                <Th>Email inviata</Th>
                <Th>Messaggio</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1D8]">
              {data.contacts.length === 0 ? (
                <EmptyRow cols={5} />
              ) : (
                data.contacts.map((c) => (
                  <tr key={c.id} className="hover:bg-[#FBF9F6]/60">
                    <Td className="whitespace-nowrap text-[#5C5C5C]">
                      {fmtDate(c.created_at)}
                    </Td>
                    <Td className="font-medium">{c.name}</Td>
                    <Td className="text-[#5C5C5C]">{c.email}</Td>
                    <Td>
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${
                          c.email_sent
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {c.email_sent ? "Sì" : "No"}
                      </span>
                    </Td>
                    <Td className="max-w-[420px] text-[#5C5C5C] whitespace-pre-wrap">
                      {c.message}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>

        {/* RSVPs */}
        <Section
          title="RSVP evento"
          icon={CalendarCheck}
          count={data.rsvps.length}
          testid="section-rsvps"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F6] border-b border-[#E5E1D8]">
              <tr>
                <Th>Data</Th>
                <Th>Nome</Th>
                <Th>Client ID</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1D8]">
              {data.rsvps.length === 0 ? (
                <EmptyRow cols={3} />
              ) : (
                data.rsvps.map((r) => (
                  <tr key={r.id || r.client_id} className="hover:bg-[#FBF9F6]/60">
                    <Td className="whitespace-nowrap text-[#5C5C5C]">
                      {fmtDate(r.created_at)}
                    </Td>
                    <Td>{r.name || "—"}</Td>
                    <Td className="font-mono text-[12px] text-[#5C5C5C]">
                      {r.client_id}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>
      </main>
    </div>
  );
};

export const AdminDashboard = () => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    toast.success("Sei uscito");
  };

  if (!token) {
    return <LoginScreen onAuthed={setToken} />;
  }
  return <Dashboard token={token} onLogout={handleLogout} />;
};

export default AdminDashboard;
